import React, { useState } from 'react';
import { MessageCircle, Phone, X, User, Calendar, Users, MapPin, Send, Clock, Baby } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { trackWhatsAppClick } from '../utils/trackWhatsApp';
import { Link } from 'react-router-dom';

const FloatingActionWidget = () => {
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    destination: '',
    travelDate: '',
    tripDuration: '',
    travelers: '2',
    kids: '0',
    message: ''
  });

  const whatsappNumber = '919816154248';
  const phoneNumber = '9816154248';

  const destinations = [
    'Dharamshala',
    'Dalhousie',
    'Dharamshala + Dalhousie',
    'Dharamshala + Dalhousie + Amritsar',
    'Shimla',
    'Manali',
    'Shimla + Manali',
    'Amritsar',
    'Other'
  ];

  const tripDurations = [
    '3 Nights 4 Days',
    '4 Nights 5 Days',
    '5 Nights 6 Days',
    '6 Nights 7 Days',
    '7 Nights 8 Days',
    'Custom Duration'
  ];

  const handleWhatsAppClick = () => {
    const whatsappMessage = 'Hi, I want to plan my Himachal trip with Dharamshala Dalhousie Tour Package.';
    trackWhatsAppClick({ location: 'floating-quick' });
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:+91${phoneNumber}`;
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Save lead to backend
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          destination: formData.destination,
          travel_date: formData.travelDate,
          trip_duration: formData.tripDuration,
          travelers: formData.travelers,
          kids: formData.kids,
          message: formData.message || '',
          source: 'floating_widget'
        })
      });
    } catch (error) {
      console.log('Failed to save lead to backend');
    }
    
    const message = `🏔️ *New Trip Enquiry*

👤 *Name:* ${formData.name}
📞 *Phone:* ${formData.phone}
📍 *Destination:* ${formData.destination}
🕐 *Duration:* ${formData.tripDuration}
📅 *Travel Date:* ${formData.travelDate}
👥 *Adults:* ${formData.travelers}
👶 *Kids:* ${formData.kids}
${formData.message ? `💬 *Message:* ${formData.message}` : ''}

_Sent from website_`;

    trackWhatsAppClick({ location: 'floating-form-submit', packageName: formData.destination });
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
    setShowQuoteForm(false);
    setFormData({ name: '', phone: '', destination: '', travelDate: '', tripDuration: '', travelers: '2', kids: '0', message: '' });
  };

  return (
    <>
      {/* Quick Quote Form Popup */}
      {showQuoteForm && (
        <div className="fixed bottom-24 right-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-scale-in max-h-[80vh] overflow-y-auto">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3 text-white flex items-center justify-between sticky top-0 z-10">
            <h3 className="font-bold">Quick Trip Quote</h3>
            <button 
              onClick={() => setShowQuoteForm(false)}
              className="w-6 h-6 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center"
              data-testid="floating-close-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-4 space-y-3" data-testid="floating-quote-form">
            <div>
              <Label className="text-xs text-gray-600 flex items-center gap-1">
                <User className="h-3 w-3" /> Name *
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Your name"
                required
                className="mt-1 h-9 text-sm rounded-lg"
                data-testid="floating-name-input"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600 flex items-center gap-1">
                <Phone className="h-3 w-3" /> Phone *
              </Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+91 XXXXX XXXXX"
                required
                className="mt-1 h-9 text-sm rounded-lg"
                data-testid="floating-phone-input"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> Destination *
              </Label>
              <select
                value={formData.destination}
                onChange={(e) => handleChange('destination', e.target.value)}
                required
                className="mt-1 w-full h-9 px-3 text-sm rounded-lg border border-gray-200 focus:border-orange-500 outline-none"
                data-testid="floating-destination-select"
              >
                <option value="">Select destination</option>
                {destinations.map(dest => (
                  <option key={dest} value={dest}>{dest}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-600 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Duration *
              </Label>
              <select
                value={formData.tripDuration}
                onChange={(e) => handleChange('tripDuration', e.target.value)}
                required
                className="mt-1 w-full h-9 px-3 text-sm rounded-lg border border-gray-200 focus:border-orange-500 outline-none"
                data-testid="floating-duration-select"
              >
                <option value="">Select duration</option>
                {tripDurations.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-gray-600 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Date *
                </Label>
                <Input
                  type="date"
                  value={formData.travelDate}
                  onChange={(e) => handleChange('travelDate', e.target.value)}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-1 h-9 text-sm rounded-lg"
                  data-testid="floating-date-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600 flex items-center gap-1">
                  <Users className="h-3 w-3" /> Adults
                </Label>
                <select
                  value={formData.travelers}
                  onChange={(e) => handleChange('travelers', e.target.value)}
                  className="mt-1 w-full h-9 px-3 text-sm rounded-lg border border-gray-200 focus:border-orange-500 outline-none"
                  data-testid="floating-travelers-select"
                >
                  {[1,2,3,4,5,6,7,8,9,'10+'].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-600 flex items-center gap-1">
                <Baby className="h-3 w-3" /> Kids
              </Label>
              <select
                value={formData.kids}
                onChange={(e) => handleChange('kids', e.target.value)}
                className="mt-1 w-full h-9 px-3 text-sm rounded-lg border border-gray-200 focus:border-orange-500 outline-none"
                data-testid="floating-kids-select"
              >
                {[0,1,2,3,4,5,'6+'].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-600 flex items-center gap-1">
                <MessageCircle className="h-3 w-3" /> Message
              </Label>
              <textarea
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                placeholder="Any special requirements?"
                rows={2}
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-orange-500 outline-none resize-none"
                data-testid="floating-message-textarea"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg font-semibold"
              data-testid="floating-submit-btn"
            >
              <Send className="mr-2 h-4 w-4" />
              Send to WhatsApp
            </Button>
          </form>
        </div>
      )}

      {/* Floating Buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 animate-fade-in">
        {/* Direct WhatsApp Button */}
        <button
          onClick={handleWhatsAppClick}
          className="w-12 h-12 bg-[#25D366] hover:bg-[#20bd5a] rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center hover:scale-110"
          aria-label="Chat on WhatsApp"
          data-testid="floating-whatsapp-btn"
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6 text-white" fill="currentColor" aria-hidden="true">
            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.767.967-.94 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
          </svg>
        </button>

        {/* Plan My Trip Button (opens the /quote planner) */}
        <Link
          to="/quote"
          className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 group"
          aria-label="Plan My Trip"
          data-testid="floating-get-quote-btn"
        >
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-orange-500/30">
            <MessageCircle className="h-5 w-5 text-white" />
          </div>
          <span className="hidden md:block text-sm font-semibold pr-2">Plan My Trip</span>
        </Link>

        {/* Phone Call Button */}
        <button
          onClick={handlePhoneClick}
          className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center hover:scale-110"
          aria-label="Call us"
          data-testid="floating-call-btn"
        >
          <Phone className="h-5 w-5 text-white" />
        </button>
      </div>
    </>
  );
};

export default FloatingActionWidget;
